import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Eye, EyeOff, Mail, Lock } from 'lucide-react';
import { toast } from 'sonner';
interface LoginFormProps {
  onToggleForm: () => void;
}
const LoginForm = ({
  onToggleForm
}: LoginFormProps) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }
    if (!email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }
    setIsLoading(true);
    try {
      const {
        data,
        error: authError
      } = await supabase.auth.signInWithPassword({
        email: email.toLowerCase().trim(),
        password
      });
      if (authError) {
        if (authError.message.includes('Invalid login credentials')) {
          setError('Incorrect email or password. Please check your credentials and try again.');
        } else if (authError.message.includes('Email not confirmed')) {
          setError('Please check your email and click the confirmation link before signing in.');
        } else {
          setError(authError.message);
        }
        return;
      }
      if (data.user) {
        toast.success('Welcome back to Ọmọ Odùduwà Community!');
        navigate('/community');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  return <form onSubmit={handleLogin} className="space-y-6">
      <div className="space-y-4">
        <div>
          <Label htmlFor="email" className="text-sm font-medium text-gray-700 flex items-center gap-2">
            <Mail size={16} />
            Email Address
          </Label>
          <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Enter your email address" disabled={isLoading} autoComplete="email" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-omoduduwa-terracotta focus:ring-omoduduwa-terracotta bg-gray-50" />
        </div>

        <div>
          <Label htmlFor="password" className="text-sm font-medium text-gray-700 flex items-center gap-2">
            <Lock size={16} />
            Password
          </Label>
          <div className="mt-1 relative">
            <Input id="password" type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter your password" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-omoduduwa-terracotta focus:ring-omoduduwa-terracotta pr-10" disabled={isLoading} autoComplete="current-password" />
            <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setShowPassword(!showPassword)} disabled={isLoading}>
              {showPassword ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
            </button>
          </div>
        </div>
      </div>

      {error && <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>}

      <Button type="submit" className="w-full bg-omoduduwa-terracotta hover:bg-omoduduwa-terracotta/90 text-white font-medium py-2 px-4 rounded-md transition-colors" disabled={isLoading}>
        {isLoading ? <div className="flex items-center justify-center">
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
            Signing in...
          </div> : 'Sign In'}
      </Button>

      <div className="text-center">
        <p className="text-sm text-gray-600">
          Don't have an account?{' '}
          <button type="button" onClick={onToggleForm} className="font-medium text-omoduduwa-terracotta hover:text-omoduduwa-terracotta/80 transition-colors" disabled={isLoading}>
            Create Account
          </button>
        </p>
      </div>
    </form>;
};
export default LoginForm;